import type {
  FoundationPath,
  FoundationSection,
  GeneratedFoundationSubSystem,
  GeneratedFoundationSubSystemAny,
  GeneratedFoundationSuperSystem,
  LinearFoundationSection,
  Slab,
} from "building-types/foundations/foundationTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { aabb2ByPolyLine2s, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { Extrusion } from "geom/brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { polygonIntersection, polygonSubtract, polygonUnion } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2AABB2, polyLine2ByTwoPoints, polyLine2HasNoNaN } from "geom/polyLine2";
import { polygonArea } from "geom/polyLine2Polygon";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { Rect2 } from "geom/rect2Types";
import {
  isCurbSectionComponent,
  isFootingSectionComponent,
  isInsetSectionType,
  isIsolatedFoundationSection,
  isLedgeSectionType,
  isLinearFoundationSection,
  isPierSectionComponent,
  isStemWallSectionComponent,
} from "./foundationStrip";
import { foundationPathCoordinates } from "./pathCoordinates";
import { isStemWallInsetSection } from "./stemWall";

export interface ExtractedFoundationSectionGeometry {
  readonly planProfiles: PolyLine2[];
  readonly extrusions: Extrusion[];
  readonly path: FoundationPath;
  readonly section: FoundationSection;
}

export type FoundationSectionGeometryMap = { [foundationSystemId: string]: ExtractedFoundationSectionGeometry[] };

/**
 * Extracts planProfiles, extrusions, and path from all linear and isolated
 * foundation sections in the foundations table, mapped by foundation system id.
 */
export function extractFoundationSectionGeometry(
  foundations: Table<GeneratedFoundationSubSystemAny>,
): FoundationSectionGeometryMap {
  const result: FoundationSectionGeometryMap = {};

  for (const [foundationSystemId, foundationSystem] of Object.entries(foundations)) {
    const { strips } = foundationSystem.subSystem;
    const geometries: ExtractedFoundationSectionGeometry[] = [];

    for (const strip of Object.values(strips)) {
      for (const section of strip.sections) {
        geometries.push({
          planProfiles: section.planProfiles,
          extrusions: section.extrusions,
          path: section.path,
          section,
        });
      }
    }

    result[foundationSystemId] = geometries;
  }

  return result;
}

// Helper functions for the new split foundation data structure

export function getFoundationSlabsFromSplit(
  _foundationSubSystem: GeneratedFoundationSubSystem,
  foundationSuperSystem: GeneratedFoundationSuperSystem,
): Slab[] {
  return foundationSuperSystem.superSystem.slabs;
}

export function getFoundationSectionsFromSplit(
  foundationSubSystem: GeneratedFoundationSubSystemAny,
): FoundationSection[] {
  const found: FoundationSection[] = [];
  const { subSystem } = foundationSubSystem;
  Object.values(subSystem.strips).forEach((strip) => {
    const { sections } = strip;
    found.push(...sections);
  });
  return found;
}

export function foundationSectionConditionLine(
  section: FoundationSection,
  planarGraphs: Table<PolygonPlanarGraph>,
): PolyLine2 {
  const { path } = section;
  const [startX, startY, endX, endY] = foundationPathCoordinates(path, planarGraphs);
  return polyLine2ByTwoPoints(startX, startY, endX, endY);
}

export function sectionStartCapPolyLine2(section: LinearFoundationSection): PolyLine2 {
  return polyLine2New(section.startCap.verts);
}

export function sectionEndCapPolyLine2(footing: LinearFoundationSection): PolyLine2 {
  return polyLine2New(footing.endCap.verts);
}

function averagePoint(values: number[]): { x: number; y: number } {
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  for (let i = 0; i < values.length - 1; i += 2) {
    sumX += values[i];
    sumY += values[i + 1];
    count += 1;
  }
  if (count === 0) {
    return { x: 0, y: 0 };
  }
  return { x: sumX / count, y: sumY / count };
}

export function profileWithoutCapAdjacentEdge(profile: Rect2, capVerts: number[]): PolyLine2 {
  if (profile.length < 10) {
    return profile;
  }

  const p0x = profile[0];
  const p0y = profile[1];
  const p1x = profile[2];
  const p1y = profile[3];
  const p2x = profile[4];
  const p2y = profile[5];
  const p3x = profile[6];
  const p3y = profile[7];
  const points: readonly [number, number][] = [
    [p0x, p0y],
    [p1x, p1y],
    [p2x, p2y],
    [p3x, p3y],
  ];
  const capCenter = averagePoint(capVerts);

  let removeIndex = 0;
  let bestDistanceSquared = Number.POSITIVE_INFINITY;
  for (let i = 0; i < points.length; i++) {
    const [ax, ay] = points[i];
    const [bx, by] = points[(i + 1) % points.length];
    const midX = (ax + bx) / 2;
    const midY = (ay + by) / 2;
    const dx = midX - capCenter.x;
    const dy = midY - capCenter.y;
    const distSquared = dx * dx + dy * dy;
    if (distSquared < bestDistanceSquared) {
      bestDistanceSquared = distSquared;
      removeIndex = i;
    }
  }

  const startIndex = (removeIndex + 1) % points.length;
  const out: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[(startIndex + i) % points.length];
    out.push(x, y);
  }
  return polyLine2New(out);
}

function sectionPlanProfilesWithoutCaps(section: LinearFoundationSection, includeGaps = true): PolyLine2[] {
  if (section.planProfiles.length <= 1 || !includeGaps) {
    return [section.rightEdgeGaps.edge, section.leftEdgeGaps.edge];
  }

  const profiles: PolyLine2[] = [];

  profiles.push(profileWithoutCapAdjacentEdge(section.planProfiles[0], section.startCap.verts));

  for (let i = 1, l = section.planProfiles.length; i < l - 1; i++) {
    profiles.push(section.planProfiles[i]);
  }

  profiles.push(
    profileWithoutCapAdjacentEdge(section.planProfiles[section.planProfiles.length - 1], section.endCap.verts),
  );

  return profiles;
}

export function sectionGapEnds(section: FoundationSection): PolyLine2[] {
  if (section.planProfiles.length <= 1) {
    return [];
  }

  const gapEnds: PolyLine2[] = [];

  gapEnds.push(section.planProfiles[0].slice(4, 8));

  for (let i = 1, l = section.planProfiles.length; i < l - 1; i++) {
    gapEnds.push(section.planProfiles[i].slice(0, 5));
    gapEnds.push(section.planProfiles[i].slice(4, 8));
  }

  gapEnds.push(section.planProfiles[section.planProfiles.length - 1].slice(0, 5));

  return gapEnds;
}

// this is faster than the union approach, but will potentially produce incorrect linework
export function sectionOutlinesFast(sections: FoundationSection[], includeGaps = true) {
  const planProfiles: PolyLine2[] = [];
  for (const section of sections) {
    if (!isLinearFoundationSection(section)) {
      continue;
    }
    if (section.startCap.isFreeEnd) {
      planProfiles.push(sectionStartCapPolyLine2(section));
    } else if (section.startCap.offsetDifferenceOutline) {
      planProfiles.push(polyLine2New(section.startCap.offsetDifferenceOutline));
    }

    if (section.endCap.isFreeEnd) {
      planProfiles.push(sectionEndCapPolyLine2(section));
    } else if (section.endCap.offsetDifferenceOutline) {
      planProfiles.push(polyLine2New(section.endCap.offsetDifferenceOutline));
    }

    planProfiles.push(...sectionPlanProfilesWithoutCaps(section, includeGaps));
  }

  return planProfiles;
}

export function foundationOutlines(foundationSuperSystem: GeneratedFoundationSuperSystem, union = true) {
  const planProfiles: PolyLine2[] = [];
  const planProfilesByType: { [K in RoomClass]?: PolyLine2[] } = {};
  for (const { roomClass, exteriorProfile } of foundationSlabs(foundationSuperSystem)) {
    if (!planProfilesByType[roomClass]) {
      planProfilesByType[roomClass] = [];
    }
    planProfilesByType[roomClass].push(exteriorProfile[0]);
  }
  for (const roomClass in planProfilesByType) {
    const slabProfiles = planProfilesByType[roomClass];
    if (union) {
      planProfiles.push(...planProfileUnion(slabProfiles));
    } else {
      planProfiles.push(...slabProfiles);
    }
  }
  return planProfiles;
}

function planFlooringProfilesByRoomClassType(foundationSuperSystem: GeneratedFoundationSuperSystem) {
  const planProfilesByType: { [K in RoomClass]?: { [floorElevation: number]: PolyLine2Polygon[] } } = {};
  for (const {
    roomClass,
    planProfile,
    size: { top },
  } of foundationSlabs(foundationSuperSystem)) {
    if (!planProfilesByType[roomClass]) {
      planProfilesByType[roomClass] = {};
    }
    if (!planProfilesByType[roomClass][top]) {
      planProfilesByType[roomClass][top] = [];
    }
    planProfilesByType[roomClass][top].push(planProfile);
  }
  return planProfilesByType;
}

export function slabOutlines(foundationSuperSystem: GeneratedFoundationSuperSystem, union = true) {
  const planProfiles: PolyLine2[] = [];
  const planProfilesByType = planFlooringProfilesByRoomClassType(foundationSuperSystem);
  for (const key in planProfilesByType) {
    const roomClass: RoomClass = +key;
    const planProfilesByElevation = planProfilesByType[roomClass];
    for (const floorElevation in planProfilesByElevation) {
      const slabProfiles = planProfilesByElevation[floorElevation];
      if (union) {
        polygonUnion(slabProfiles).forEach((p) => planProfiles.push(...p));
      } else {
        slabProfiles.forEach((p) => planProfiles.push(...p));
      }
    }
  }
  return planProfiles;
}

interface ProfileSplitInfo {
  initialProfile: PolyLine2;
  subtractProfiles?: PolyLine2[];
  mergeProfiles?: PolyLine2[];
  intersectionProfiles?: PolyLine2[];
}

function extractArrayOfInitialProfiles(planProfileTable: { [height: number]: ProfileSplitInfo[] }) {
  const initialProfiles: PolyLine2[] = [];
  for (const height in planProfileTable) {
    const splitInfo = planProfileTable[height];
    for (const info of splitInfo) {
      initialProfiles.push(info.initialProfile);
    }
  }
  return initialProfiles;
}

function accumulateOverlappingProfile(
  baseProfile: PolyLine2[],
  aabbBaseProfile: AABB2,
  accumulatedProfile: PolyLine2[] | undefined,
) {
  for (const profile of baseProfile) {
    const aabbProfile = polyLine2AABB2(profile);
    const overlaps = aabb2Overlaps(aabbProfile, aabbBaseProfile);
    if (overlaps) {
      if (!accumulatedProfile) {
        accumulatedProfile = [];
      }
      accumulatedProfile.push(profile);
    }
  }
  return accumulatedProfile;
}

function subtractIsolatedFoundationFromPlanProfile(
  foundationSubSystem: GeneratedFoundationSubSystemAny,
  planProfileTable: { [height: number]: ProfileSplitInfo[] },
  subtractIsolated: boolean = true,
  subtractPiers: boolean = true,
) {
  const resPlanProfiles: { [height: number]: ProfileSplitInfo[] } = {};
  const planProfileIsolated: { [height: number]: PolyLine2[] } = {};
  const planProfilePier: { [height: number]: PolyLine2[] } = {};

  if (subtractIsolated) {
    for (const footing of foundationFootings(foundationSubSystem)) {
      const footingTop = footing.size.top;
      if (isIsolatedFoundationSection(footing)) {
        if (!planProfileIsolated[footingTop]) {
          planProfileIsolated[footingTop] = [];
        }
        planProfileIsolated[footingTop].push(...footing.planProfiles);
      }
    }
  }
  if (subtractPiers) {
    for (const pier of foundationPiers(foundationSubSystem)) {
      const pierTop = pier.size.top;
      if (!planProfilePier[pierTop]) {
        planProfilePier[pierTop] = [];
      }
      planProfilePier[pierTop].push(...pier.planProfiles);
    }
  }
  for (const height in planProfileTable) {
    const planProfiles = planProfileTable[height];
    for (const planProfile of planProfiles) {
      const aabbPlanProfile = polyLine2AABB2(planProfile.initialProfile);
      for (const footingTop in planProfileIsolated) {
        if (Number(footingTop) < Number(height) + DIST_EPSILON) {
          continue;
        }
        //only consider subtracting if the footingTop of isolated foundation is above the plan profile
        const isolatedProfiles = planProfileIsolated[footingTop];
        planProfile.subtractProfiles = accumulateOverlappingProfile(
          isolatedProfiles,
          aabbPlanProfile,
          planProfile.subtractProfiles,
        );
      }
      for (const columnTop in planProfilePier) {
        if (Number(columnTop) < Number(height) + DIST_EPSILON) {
          continue;
        }
        //only consider subtracting if the columnTop of pier is above the plan profile
        const pierProfiles = planProfilePier[columnTop];
        planProfile.subtractProfiles = accumulateOverlappingProfile(
          pierProfiles,
          aabbPlanProfile,
          planProfile.subtractProfiles,
        );
      }
    }
  }
  for (const height in planProfileTable) {
    for (const planProfile of planProfileTable[height]) {
      if (!planProfile.subtractProfiles) {
        if (resPlanProfiles[height] === undefined) {
          resPlanProfiles[height] = [];
        }
        resPlanProfiles[height].push({ initialProfile: planProfile.initialProfile });
      } else {
        const splittedPolygons = polygonSubtract([[planProfile.initialProfile, ...planProfile.subtractProfiles]]);
        for (const splittedPolygon of splittedPolygons) {
          if (polygonArea(splittedPolygon) > DIST_EPSILON_SQUARED) {
            for (const polygon of splittedPolygon) {
              if (resPlanProfiles[height] === undefined) {
                resPlanProfiles[height] = [];
              }
              resPlanProfiles[height].push({ initialProfile: polygon });
            }
          }
        }
      }
    }
  }
  return resPlanProfiles;
}

export function brickLedgeOutlines(foundationSubSystem: GeneratedFoundationSubSystemAny, union = true) {
  const planProfileBrickLedgeTable: { [height: number]: ProfileSplitInfo[] } = {};
  for (const brickLedge of foundationBrickLedges(foundationSubSystem)) {
    const height = brickLedge.size.top - brickLedge.size.depth;
    if (!planProfileBrickLedgeTable[height]) {
      planProfileBrickLedgeTable[height] = [];
    }
    for (const profile of brickLedge.planProfiles) {
      planProfileBrickLedgeTable[height].push({ initialProfile: profile });
    }
  }
  const planProfiles = extractArrayOfInitialProfiles(
    subtractIsolatedFoundationFromPlanProfile(foundationSubSystem, planProfileBrickLedgeTable),
  );

  if (!union) {
    return planProfiles;
  }
  return planProfileUnion(planProfiles);
}

enum ProfileOperation {
  MERGE = "MERGE",
  SUBTRACT = "SUBTRACT",
  INTERSECTION = "INTERSECTION",
}

function updatePlanProfilesSplitInfo(
  planProfileMapLinear: ProfileSplitInfo[],
  planProfileMapIsolated: ProfileSplitInfo[],
  operation: ProfileOperation,
) {
  for (const planProfileLinear of planProfileMapLinear) {
    const { initialProfile: intialProfileLinear } = planProfileLinear;
    const aabbLinear = polyLine2AABB2(intialProfileLinear);
    for (const planProfileIsolated of planProfileMapIsolated) {
      const { initialProfile: intialProfileIsolated } = planProfileIsolated;
      const aabbIsolated = polyLine2AABB2(intialProfileIsolated);
      const overlaps = aabb2Overlaps(aabbIsolated, aabbLinear);
      if (overlaps) {
        switch (operation) {
          case ProfileOperation.MERGE: {
            if (!planProfileLinear.mergeProfiles) {
              planProfileLinear.mergeProfiles = [];
            }
            if (!planProfileIsolated.mergeProfiles) {
              planProfileIsolated.mergeProfiles = [];
            }
            planProfileLinear.mergeProfiles.push(intialProfileIsolated); //these have the same footing top and needs merging
            planProfileIsolated.mergeProfiles.push(intialProfileLinear); //these have the same footing top
            break;
          }
          case ProfileOperation.SUBTRACT: {
            if (!planProfileLinear.subtractProfiles) {
              planProfileLinear.subtractProfiles = [];
            }
            planProfileLinear.subtractProfiles.push(intialProfileIsolated);
            break;
          }
          case ProfileOperation.INTERSECTION: {
            if (!planProfileIsolated.intersectionProfiles) {
              planProfileIsolated.intersectionProfiles = [];
            }
            planProfileIsolated.intersectionProfiles.push(intialProfileLinear);
            break;
          }
        }
      }
    }
  }
}

export function footingOutlines(foundationSubSystem: GeneratedFoundationSubSystemAny, union = true) {
  const planProfiles: PolyLine2[] = [];
  const planProfileMapLinearInitial: { [height: number]: ProfileSplitInfo[] } = {};
  const planProfileMapIsolated: { [height: number]: ProfileSplitInfo[] } = {};
  for (const footing of foundationFootings(foundationSubSystem)) {
    const footingTop = footing.size.top;
    if (isLinearFoundationSection(footing)) {
      if (!planProfileMapLinearInitial[footingTop]) {
        planProfileMapLinearInitial[footingTop] = [];
      }
      for (const profile of footing.planProfiles) {
        planProfileMapLinearInitial[footingTop].push({ initialProfile: profile });
      }
    } else if (isIsolatedFoundationSection(footing)) {
      if (!planProfileMapIsolated[footingTop]) {
        planProfileMapIsolated[footingTop] = [];
      }
      for (const profile of footing.planProfiles) {
        planProfileMapIsolated[footingTop].push({ initialProfile: profile });
      }
    }
  }
  if (!union) {
    Object.values(planProfileMapLinearInitial).map((p) => {
      p.map(({ initialProfile }) => planProfiles.push(initialProfile));
    });
    Object.values(planProfileMapIsolated).map((p) => {
      p.map(({ initialProfile }) => planProfiles.push(initialProfile));
    });
    return planProfiles;
  }

  //this step is a kind of hack as for some reason footings are not coming subtracted
  //this may be some bug in the foundation section generation code - TODO: investigate
  //noting this as a bug because it seems to be sensitive to the width of the pier and hence
  //subtracting the pier width from the plan profile seems to fix the issue
  const planProfileMapLinear = subtractIsolatedFoundationFromPlanProfile(
    foundationSubSystem,
    planProfileMapLinearInitial,
    false,
  );

  //identify profiles which needs to be subtracted and merged with linear footings
  for (const keyLinear in planProfileMapLinear) {
    for (const keyIsolated in planProfileMapIsolated) {
      if (Number(keyLinear) < Number(keyIsolated)) {
        updatePlanProfilesSplitInfo(
          planProfileMapLinear[keyLinear],
          planProfileMapIsolated[keyIsolated],
          ProfileOperation.SUBTRACT,
        );
      } else if (Number(keyLinear) > Number(keyIsolated)) {
        updatePlanProfilesSplitInfo(
          planProfileMapLinear[keyLinear],
          planProfileMapIsolated[keyIsolated],
          ProfileOperation.INTERSECTION,
        );
      } else {
        //same height
        updatePlanProfilesSplitInfo(
          planProfileMapLinear[keyLinear],
          planProfileMapIsolated[keyIsolated],
          ProfileOperation.MERGE,
        );
      }
    }
  }

  Object.values(planProfileMapLinear).map((p) =>
    p.forEach((current) => {
      if (current.mergeProfiles) {
        planProfiles.push(...planProfileUnion([current.initialProfile, ...current.mergeProfiles]));
      }
    }),
  );

  Object.values(planProfileMapLinear).map((p) =>
    p.forEach((current) => {
      if (current.subtractProfiles) {
        const splittedPolygons = polygonSubtract([[current.initialProfile, ...current.subtractProfiles]]);
        for (const splittedPolygon of splittedPolygons) {
          if (polygonArea(splittedPolygon) > DIST_EPSILON_SQUARED) {
            planProfiles.push(...splittedPolygon);
          }
        }
      }
    }),
  );
  Object.values(planProfileMapLinear).map((p) =>
    p.forEach((current) => {
      if (!current.subtractProfiles && !current.mergeProfiles && !current.intersectionProfiles) {
        planProfiles.push(...[current.initialProfile]);
      }
    }),
  );

  const planProfilesIntersected: PolyLine2[] = [];
  Object.values(planProfileMapIsolated).map((p) =>
    p.forEach((current) => {
      if (current.intersectionProfiles) {
        const intersectedPolygons = polygonIntersection([[current.initialProfile, ...current.intersectionProfiles]]);
        for (const intersectedPolygon of intersectedPolygons) {
          if (polygonArea(intersectedPolygon) > DIST_EPSILON_SQUARED) {
            planProfilesIntersected.push(...intersectedPolygon);
          }
        }
      }
    }),
  );

  const planProfilesCompletelyIsolated: PolyLine2[] = [];
  Object.values(planProfileMapIsolated).map((p) =>
    p.forEach((current) => {
      if (!current.subtractProfiles && !current.mergeProfiles && !current.intersectionProfiles) {
        planProfilesCompletelyIsolated.push(...[current.initialProfile]);
      }
    }),
  );

  return [...planProfileUnion(planProfiles), ...planProfilesIntersected, ...planProfilesCompletelyIsolated];
}

export function stemWallComponentOutlines(foundationSubSystem: GeneratedFoundationSubSystemAny, union = true) {
  const planProfileStemWallTable: { [height: number]: ProfileSplitInfo[] } = {};

  for (const stemWall of foundationStemWalls(foundationSubSystem)) {
    // we are looking for stemwall sections that are below the slab
    if (isInsetSectionType(stemWall)) {
      continue;
    }
    const height = stemWall.size.top - stemWall.size.depth;
    if (!planProfileStemWallTable[height]) {
      planProfileStemWallTable[height] = [];
    }
    for (const profile of stemWall.planProfiles) {
      planProfileStemWallTable[height].push({ initialProfile: profile });
    }
  }

  const planProfiles = extractArrayOfInitialProfiles(
    subtractIsolatedFoundationFromPlanProfile(foundationSubSystem, planProfileStemWallTable),
  );

  if (!union) {
    return planProfiles;
  }

  return planProfileUnion(planProfiles);
}

interface PierSectionFlooringPolygonPair {
  pierSection: FoundationSection;
  flooringPolygon: PolyLine2Polygon;
}

export function pierOutlines(
  foundationSuperSystem: GeneratedFoundationSuperSystem,
  foundationSubSystem: GeneratedFoundationSubSystem,
): { solid: PolyLine2[]; dashed: PolyLine2[] } {
  const planProfilesSolid: PolyLine2[] = [];
  const planProfilesDashed: PolyLine2[] = [];
  const piers = foundationPiers(foundationSubSystem);
  if (piers.length === 0) {
    return { solid: planProfilesSolid, dashed: planProfilesDashed };
  }

  const planFlooringProfiles = planFlooringProfilesByRoomClassType(foundationSuperSystem);
  const planFlooringProfilesHt: { [height: number]: PolyLine2Polygon[] } = {};

  for (const key in planFlooringProfiles) {
    for (const heightKey in planFlooringProfiles[key]) {
      if (!planFlooringProfilesHt[heightKey]) {
        planFlooringProfilesHt[heightKey] = [];
      }
      planFlooringProfilesHt[heightKey].push(...planFlooringProfiles[key][heightKey]);
    }
  }

  const overlappingPierPolygonArray: PierSectionFlooringPolygonPair[] = [];
  const overlapCount = new Array(piers.length).fill(0);

  for (const heightKey in planFlooringProfilesHt) {
    const flooringPolygonsAtSameHt = planFlooringProfilesHt[heightKey];
    for (const flooringPolygon of flooringPolygonsAtSameHt) {
      const aabbFlooringPoly = aabb2ByPolyLine2s(flooringPolygon);
      piers.forEach((pier, index) => {
        if (Number(heightKey) > pier.size.top) {
          const aabbPier = aabb2ByPolyLine2s(pier.planProfiles);
          const overlaps = aabb2Overlaps(ensureDefined(aabbFlooringPoly), ensureDefined(aabbPier));
          if (overlaps) {
            overlappingPierPolygonArray.push({ pierSection: pier, flooringPolygon: flooringPolygon });
            overlapCount[index] += 1;
          }
        }
      });
    }
  }

  if (overlappingPierPolygonArray.length) {
    for (const { pierSection, flooringPolygon } of overlappingPierPolygonArray) {
      for (const planProfile of pierSection.planProfiles) {
        const intersectedPolygons = polygonIntersection([[planProfile], flooringPolygon]);
        for (const intersectedPolygon of intersectedPolygons) {
          if (polygonArea(intersectedPolygon) > DIST_EPSILON_SQUARED) {
            planProfilesDashed.push(...intersectedPolygon);
          }
        }
        const subtractedPolygons = polygonSubtract([[planProfile], flooringPolygon]);
        for (const subtractedPolygon of subtractedPolygons) {
          if (polygonArea(subtractedPolygon) > DIST_EPSILON_SQUARED) {
            planProfilesSolid.push(...subtractedPolygon);
          }
        }
      }
    }
  }

  piers.forEach((pier, index) => {
    if (overlapCount[index] === 0) {
      planProfilesSolid.push(...pier.planProfiles);
    }
  });

  return { solid: planProfilesSolid, dashed: planProfilesDashed };
}

export function stemWallInsetOutlines(foundationSubSystem: GeneratedFoundationSubSystemAny, union = true) {
  const planProfileTable: { [height: number]: ProfileSplitInfo[] } = {};
  for (const stemWall of foundationStemWalls(foundationSubSystem)) {
    // we are looking for stemwall sections that are at the height of the slab or
    // above, since they will have openings on them
    if (!isInsetSectionType(stemWall)) {
      continue;
    }
    const height = stemWall.size.top - stemWall.size.depth;
    if (!planProfileTable[height]) {
      planProfileTable[height] = [];
    }
    for (const profile of stemWall.planProfiles) {
      planProfileTable[height].push({ initialProfile: profile });
    }
  }

  for (const brickLedge of foundationBrickLedges(foundationSubSystem)) {
    // if the brickledge is already on the stem wall then it will
    // already exist in the profiles
    if (!isStemWallInsetSection(brickLedge)) {
      continue;
    }
    const height = brickLedge.size.top - brickLedge.size.depth;
    if (!planProfileTable[height]) {
      planProfileTable[height] = [];
    }
    for (const profile of brickLedge.planProfiles) {
      planProfileTable[height].push({ initialProfile: profile });
    }
  }

  const planProfiles = extractArrayOfInitialProfiles(
    subtractIsolatedFoundationFromPlanProfile(foundationSubSystem, planProfileTable),
  );

  if (!union) {
    return planProfiles;
  }

  return planProfileUnion(planProfiles);
}

export function stemWallGapOutlines(
  foundationSubSystem: GeneratedFoundationSubSystemAny,
  union = true,
  includeZeroHeightGaps = true,
) {
  const gapProfiles: PolyLine2[] = [];
  for (const stemWall of foundationStemWalls(foundationSubSystem)) {
    if (!isLinearFoundationSection(stemWall) || !isInsetSectionType(stemWall)) {
      continue;
    }
    if (includeZeroHeightGaps) {
      gapProfiles.push(...stemWall.gapProfiles);
    } else {
      gapProfiles.push(
        ...stemWall.gapProfiles.filter((_, i) => {
          return stemWall.leftEdgeGaps.gaps[i].bottom > stemWall.size.top - stemWall.size.depth;
        }),
      );
    }
  }
  for (const brickLedge of foundationBrickLedges(foundationSubSystem)) {
    if (!isLinearFoundationSection(brickLedge) || !isStemWallInsetSection(brickLedge)) {
      continue;
    }
    if (includeZeroHeightGaps) {
      gapProfiles.push(...brickLedge.gapProfiles);
    } else {
      gapProfiles.push(
        ...brickLedge.gapProfiles.filter((_, i) => {
          return brickLedge.leftEdgeGaps.gaps[i].bottom > brickLedge.size.top - brickLedge.size.depth;
        }),
      );
    }
  }

  if (!union) {
    return gapProfiles;
  }
  return planProfileUnion(gapProfiles);
}

export function brickLedgeGapOutlines(
  foundationSubSystem: GeneratedFoundationSubSystemAny,
  union = true,
  includeZeroHeightGaps = true,
) {
  const gapProfiles: PolyLine2[] = [];
  for (const brickLedge of foundationBrickLedges(foundationSubSystem)) {
    if (!isLinearFoundationSection(brickLedge)) {
      continue;
    }
    if (includeZeroHeightGaps) {
      gapProfiles.push(...brickLedge.gapProfiles);
    } else {
      gapProfiles.push(
        ...brickLedge.gapProfiles.filter((_, i) => {
          return brickLedge.leftEdgeGaps.gaps[i].bottom > brickLedge.size.top - brickLedge.size.depth;
        }),
      );
    }
  }
  if (!union) {
    return gapProfiles;
  }
  return planProfileUnion(gapProfiles);
}

export function planProfileUnion(planProfiles: PolyLine2[]): PolyLine2[] {
  if (!planProfiles.length) {
    return [];
  }

  const profilePgons: PolyLine2Polygon[] = planProfiles.filter(polyLine2HasNoNaN).map((p) => [p]);

  const union = polygonUnion(profilePgons);

  const perimeters: PolyLine2[] = [];
  for (const p of union) {
    perimeters.push(...p);
  }

  return perimeters;
}

// Foundation selectors

export function foundationPatioSlabs(foundationSuperSystem: GeneratedFoundationSuperSystem): Slab[] {
  return foundationSlabFind(foundationSuperSystem, (s) => s.roomClass === RoomClass.PATIO);
}

export function foundationSlabs(foundationSuperSystem: GeneratedFoundationSuperSystem): Slab[] {
  return foundationSlabFind(foundationSuperSystem, () => true);
}

// Legacy function for backward compatibility
export function foundationSlabsLegacy(foundationSuperSystem: GeneratedFoundationSuperSystem): Slab[] {
  return foundationSlabFind(foundationSuperSystem, () => true);
}

function foundationSlabFind(foundationSuperSystem: GeneratedFoundationSuperSystem, predicate: (slab: Slab) => boolean) {
  const { superSystem } = foundationSuperSystem;
  return superSystem.slabs.filter(predicate);
}

export function foundationBrickLedges(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, isLedgeSectionType);
}

export function foundationFootings(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, isFootingSectionComponent);
}

export function foundationPiers(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, isPierSectionComponent);
}

export function foundationStemWalls(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, isStemWallSectionComponent);
}

export function foundationCurbs(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, isCurbSectionComponent);
}

export function foundationSections(foundationSubSystem: GeneratedFoundationSubSystemAny): FoundationSection[] {
  return foundationSectionFind(foundationSubSystem, () => true);
}

function foundationSectionFind(
  foundationSubSystem: GeneratedFoundationSubSystemAny,
  predicate: (section: FoundationSection) => boolean,
): FoundationSection[] {
  const found: FoundationSection[] = [];

  const { subSystem } = foundationSubSystem;
  Object.values(subSystem.strips).forEach((strip) => {
    const { sections } = strip;
    found.push(...sections.filter(predicate));
  });

  return found;
}

export type FoundationPlanProfilesByHeight = Map<number, PolyLine2Polygon[]>;

export interface FoundationProfileWithZRange {
  readonly profiles: PolyLine2Polygon[];
  readonly zMin: number;
  readonly zMax: number;
}

export type FoundationPlanProfilesWithZRange = FoundationProfileWithZRange[];

const Z_TOLERANCE = 1e-4;

/**
 * Checks if two numbers are equal within a tolerance.
 */
function floatEquals(a: number, b: number, tolerance: number = Z_TOLERANCE): boolean {
  return Math.abs(a - b) < tolerance;
}

/**
 * Groups and unions planProfiles by their Z range (zMin/zMax).
 * Profiles with the same zMin and zMax (within floating point tolerance) are grouped together.
 * Returns the unioned profiles along with the min/max Z values for each group.
 */
export function unionFoundationPlanProfilesByZRange(
  geometries: ExtractedFoundationSectionGeometry[],
): FoundationPlanProfilesWithZRange {
  // Group planProfiles by Z range (zMin, zMax)
  const profileGroups: { profiles: PolyLine2Polygon[]; zMin: number; zMax: number }[] = [];

  /**
   * Finds an existing group with matching zMin and zMax (within tolerance).
   */
  function findMatchingGroup(
    zMin: number,
    zMax: number,
  ): { profiles: PolyLine2Polygon[]; zMin: number; zMax: number } | undefined {
    for (const group of profileGroups) {
      if (floatEquals(group.zMin, zMin) && floatEquals(group.zMax, zMax)) {
        return group;
      }
    }
    return undefined;
  }

  for (const geometry of geometries) {
    const { planProfiles, extrusions } = geometry;

    for (let i = 0; i < planProfiles.length; i++) {
      // extrusions are always 1:1 with profiles.
      // There can be extra extrusions for gap fills, but the first ones correspond
      const profile = planProfiles[i];
      const extrusion = extrusions[i];
      const originZ = extrusion.profile.plane.originZ;
      const extentZ = originZ + extrusion.dirZ;

      // Calculate the Z range of this extrusion
      const extrusionZMin = Math.min(originZ, extentZ);
      const extrusionZMax = Math.max(originZ, extentZ);

      // Find or create a group with matching Z range
      let group = findMatchingGroup(extrusionZMin, extrusionZMax);
      if (!group) {
        group = { profiles: [], zMin: extrusionZMin, zMax: extrusionZMax };
        profileGroups.push(group);
      }

      // Wrap single PolyLine2 as PolyLine2Polygon (simple polygon with no holes)
      group.profiles.push([profile]);
    }
  }

  // Union profiles in each group
  const result: FoundationPlanProfilesWithZRange = [];

  for (const group of profileGroups) {
    result.push({
      profiles: polygonUnion(group.profiles),
      zMin: group.zMin,
      zMax: group.zMax,
    });
  }

  return result;
}

export type FoundationPlanProfilesWithZRangeMap = { [foundationSystemId: string]: FoundationPlanProfilesWithZRange };

/**
 * Extracts and unions foundation planProfiles by extrusion Z range for each foundation system,
 * including Z range information for each Z range group.
 * Returns a map of foundation system id to unioned polygons with Z ranges by Z range.
 */
export function getFoundationPlanProfilesByZRange(
  foundations: Table<GeneratedFoundationSubSystemAny>,
): FoundationPlanProfilesWithZRangeMap {
  const geometryMap = extractFoundationSectionGeometry(foundations);
  const result: FoundationPlanProfilesWithZRangeMap = {};

  for (const [foundationSystemId, geometries] of Object.entries(geometryMap)) {
    result[foundationSystemId] = unionFoundationPlanProfilesByZRange(geometries);
  }

  return result;
}
